import {
  collection,
  doc,
  getDoc,
  query,
  where,
  serverTimestamp,
  runTransaction,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  COLLECTIONS,
  VALIDATION,
  LOG_TYPES,
  HEALTH_RECORD_TYPES,
} from '../config/constants';
import { AppError, FirestoreError } from '../lib/errors';

/**
 * Servicio de observaciones de salud (healthRecords).
 *
 * A diferencia de feeding/hydration (eventos inmutables tipo bitácora) o
 * appointments (compromiso futuro con estado), los healthRecords son
 * "ficha clínica liviana" — entradas EDITABLES con peso, vacuna,
 * antiparasitario, herida, checkup, medicación o nota.
 *
 * Cada record es single-pet (no multi). Foto opcional (1 por record).
 *
 * Trazabilidad: cada CRUD deja log INMUTABLE en `logs` con type='health'
 * y discriminator `payload.reason` ∈ {created, edited, deleted}. El log
 * incluye snapshot de petName y type para sobrevivir al soft-delete del
 * record.
 */

const ALLOWED_TYPES = Object.values(HEALTH_RECORD_TYPES);

// =====================================================================
// VALIDADORES
// =====================================================================

const validateTitle = (title) => {
  if (!title || typeof title !== 'string' || !title.trim()) {
    throw new AppError('El título es requerido', 'INVALID_TITLE');
  }
  if (title.length > VALIDATION.HEALTH_RECORD_TITLE_MAX) {
    throw new AppError(
      `El título supera los ${VALIDATION.HEALTH_RECORD_TITLE_MAX} caracteres`,
      'INVALID_TITLE'
    );
  }
};

const validateNotes = (notes) => {
  if (notes == null) return;
  if (typeof notes !== 'string') {
    throw new AppError('Las notas deben ser texto', 'INVALID_NOTES');
  }
  if (notes.length > VALIDATION.HEALTH_RECORD_NOTES_MAX) {
    throw new AppError(
      `Las notas superan los ${VALIDATION.HEALTH_RECORD_NOTES_MAX} caracteres`,
      'INVALID_NOTES'
    );
  }
};

const validateType = (type) => {
  if (!ALLOWED_TYPES.includes(type)) {
    throw new AppError(
      `Tipo inválido. Opciones: ${ALLOWED_TYPES.join(', ')}`,
      'INVALID_TYPE'
    );
  }
};

const validateOccurredAt = (occurredAt) => {
  if (!occurredAt) {
    throw new AppError('La fecha del evento es requerida', 'INVALID_OCCURRED_AT');
  }
  let dateValue;
  if (occurredAt instanceof Date) {
    dateValue = occurredAt;
  } else if (occurredAt instanceof Timestamp) {
    dateValue = occurredAt.toDate();
  } else if (typeof occurredAt?.toDate === 'function') {
    dateValue = occurredAt.toDate();
  } else {
    throw new AppError('Formato de fecha inválido', 'INVALID_OCCURRED_AT');
  }
  if (Number.isNaN(dateValue.getTime())) {
    throw new AppError('Formato de fecha inválido', 'INVALID_OCCURRED_AT');
  }
  // Permitimos pasado y futuro: el usuario puede registrar un evento que
  // pasó hace meses (cargar historial), o un próximo turno conocido.
};

const validateWeight = (weightKg) => {
  if (weightKg == null) return;
  if (typeof weightKg !== 'number' || !Number.isFinite(weightKg)) {
    throw new AppError('El peso debe ser un número', 'INVALID_WEIGHT');
  }
  if (
    weightKg < VALIDATION.HEALTH_RECORD_WEIGHT_MIN_KG ||
    weightKg > VALIDATION.HEALTH_RECORD_WEIGHT_MAX_KG
  ) {
    throw new AppError(
      `El peso debe estar entre ${VALIDATION.HEALTH_RECORD_WEIGHT_MIN_KG} kg y ${VALIDATION.HEALTH_RECORD_WEIGHT_MAX_KG} kg`,
      'INVALID_WEIGHT'
    );
  }
};

const validateProductName = (productName) => {
  if (productName == null) return;
  if (typeof productName !== 'string') {
    throw new AppError('El nombre del producto debe ser texto', 'INVALID_PRODUCT_NAME');
  }
  if (productName.length > VALIDATION.HEALTH_RECORD_PRODUCT_NAME_MAX) {
    throw new AppError(
      `El nombre del producto supera los ${VALIDATION.HEALTH_RECORD_PRODUCT_NAME_MAX} caracteres`,
      'INVALID_PRODUCT_NAME'
    );
  }
};

/**
 * Lee el pet referenciado y valida cross-tenant. Devuelve {name, ...} del pet
 * para que el caller pueda snapshotearlo en el log.
 */
const fetchAndValidatePet = async (petId, familyId) => {
  if (typeof petId !== 'string' || !petId) {
    throw new AppError('petId requerido', 'INVALID_PET_ID');
  }
  const snap = await getDoc(doc(db, COLLECTIONS.PETS, petId));
  if (!snap.exists()) {
    throw new AppError('La mascota no existe', 'PET_NOT_FOUND');
  }
  const data = snap.data();
  if (data.familyId !== familyId) {
    throw new AppError(
      'La mascota no pertenece al hogar',
      'CROSS_TENANT_PET'
    );
  }
  return { name: data.name || 'mascota sin nombre' };
};

const normalizeOccurredAt = (occurredAt) =>
  occurredAt instanceof Timestamp
    ? occurredAt
    : Timestamp.fromDate(occurredAt instanceof Date ? occurredAt : new Date(occurredAt));

// =====================================================================
// CRUD
// =====================================================================

/**
 * Crea un record de salud. Atómico: doc + log inmutable.
 *
 * @param {object} input
 * @param {string} input.uid
 * @param {string} input.familyId
 * @param {string} input.petId
 * @param {string} input.type            — 'weight' | 'vaccine' | ... (HEALTH_RECORD_TYPES)
 * @param {string} input.title
 * @param {string|null} input.notes
 * @param {number|null} input.weightKg   — solo aplica si type='weight' (no enforzado)
 * @param {string|null} input.productName
 * @param {Date} input.occurredAt
 * @returns {{ recordId: string }}
 */
export const createHealthRecord = async ({
  uid,
  familyId,
  petId,
  type,
  title,
  notes = null,
  weightKg = null,
  productName = null,
  occurredAt,
}) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!familyId) throw new AppError('Hogar requerido', 'INVALID_INPUT');

  validateTitle(title);
  validateNotes(notes);
  validateType(type);
  validateOccurredAt(occurredAt);
  validateWeight(weightKg);
  validateProductName(productName);
  const pet = await fetchAndValidatePet(petId, familyId);

  const occurredAtTs = normalizeOccurredAt(occurredAt);

  try {
    const recordId = await runTransaction(db, async (transaction) => {
      const recordRef = doc(collection(db, COLLECTIONS.HEALTH_RECORDS));

      transaction.set(recordRef, {
        familyId,
        petId,
        type,
        title: title.trim(),
        notes: notes ? notes.trim() : null,
        weightKg: weightKg ?? null,
        productName: productName ? productName.trim() : null,
        photoExt: null,
        occurredAt: occurredAtTs,
        deletedAt: null,
        createdBy: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId,
        feedingGroupId: null,
        petId,
        userId: uid,
        type: LOG_TYPES.HEALTH,
        timestamp: serverTimestamp(),
        payload: {
          reason: 'health_record_created',
          recordId: recordRef.id,
          recordType: type,
          title: title.trim(),
          petName: pet.name,
          weightKg: weightKg ?? null,
          productName: productName ? productName.trim() : null,
        },
      });

      return recordRef.id;
    });
    return { recordId };
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo crear la observación', err);
  }
};

/**
 * Actualiza campos editables. Atómico: update + log inmutable con reason='edited'.
 *
 * Campos editables: type, title, notes, weightKg, productName, occurredAt,
 * photoExt. NO se edita petId / familyId / createdBy.
 */
export const updateHealthRecord = async ({ uid, recordId, updates }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!recordId) throw new AppError('Record requerido', 'INVALID_INPUT');

  const ALLOWED = ['type', 'title', 'notes', 'weightKg', 'productName', 'occurredAt', 'photoExt'];
  const filtered = {};
  for (const key of Object.keys(updates || {})) {
    if (ALLOWED.includes(key)) {
      filtered[key] = updates[key];
    } else {
      throw new AppError(`Campo no editable: ${key}`, 'INVALID_FIELD');
    }
  }
  if (Object.keys(filtered).length === 0) {
    throw new AppError('No hay cambios para aplicar', 'NO_CHANGES');
  }

  if ('title' in filtered) validateTitle(filtered.title);
  if ('notes' in filtered) validateNotes(filtered.notes);
  if ('type' in filtered) validateType(filtered.type);
  if ('weightKg' in filtered) validateWeight(filtered.weightKg);
  if ('productName' in filtered) validateProductName(filtered.productName);
  if ('occurredAt' in filtered) validateOccurredAt(filtered.occurredAt);
  if ('photoExt' in filtered) {
    if (filtered.photoExt != null && typeof filtered.photoExt !== 'string') {
      throw new AppError('photoExt debe ser string o null', 'INVALID_FIELD');
    }
  }

  try {
    await runTransaction(db, async (transaction) => {
      const recordRef = doc(db, COLLECTIONS.HEALTH_RECORDS, recordId);
      const snap = await transaction.get(recordRef);
      if (!snap.exists()) {
        throw new AppError('La observación no existe', 'RECORD_NOT_FOUND');
      }
      const current = snap.data();
      if (current.deletedAt) {
        throw new AppError('La observación fue eliminada', 'RECORD_DELETED');
      }

      const patch = { ...filtered, updatedAt: serverTimestamp() };
      if ('title' in patch) patch.title = patch.title.trim();
      if ('notes' in patch) patch.notes = patch.notes ? patch.notes.trim() : null;
      if ('productName' in patch) {
        patch.productName = patch.productName ? patch.productName.trim() : null;
      }
      if ('occurredAt' in patch) {
        patch.occurredAt = normalizeOccurredAt(patch.occurredAt);
      }

      transaction.update(recordRef, patch);

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: current.familyId,
        feedingGroupId: null,
        petId: current.petId,
        userId: uid,
        type: LOG_TYPES.HEALTH,
        timestamp: serverTimestamp(),
        payload: {
          reason: 'health_record_edited',
          recordId,
          recordType: patch.type ?? current.type,
          title: patch.title ?? current.title,
          // changedFields: keys que cambiaron (para auditoría)
          changedFields: Object.keys(filtered),
        },
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo actualizar la observación', err);
  }
};

/**
 * Soft delete + log con reason='deleted'. El log incluye snapshot del title
 * + recordType para que la bitácora siga siendo legible aunque el record
 * desaparezca del listado.
 */
export const softDeleteHealthRecord = async ({ uid, recordId }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!recordId) throw new AppError('Record requerido', 'INVALID_INPUT');

  try {
    await runTransaction(db, async (transaction) => {
      const recordRef = doc(db, COLLECTIONS.HEALTH_RECORDS, recordId);
      const snap = await transaction.get(recordRef);
      if (!snap.exists()) {
        throw new AppError('La observación no existe', 'RECORD_NOT_FOUND');
      }
      const current = snap.data();
      if (current.deletedAt) {
        throw new AppError('La observación ya fue eliminada', 'RECORD_DELETED');
      }

      transaction.update(recordRef, {
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: current.familyId,
        feedingGroupId: null,
        petId: current.petId,
        userId: uid,
        type: LOG_TYPES.HEALTH,
        timestamp: serverTimestamp(),
        payload: {
          reason: 'health_record_deleted',
          recordId,
          recordType: current.type,
          title: current.title,
        },
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo eliminar la observación', err);
  }
};

// =====================================================================
// READS
// =====================================================================

export const fetchHealthRecord = async (recordId) => {
  if (!recordId) throw new AppError('Record requerido', 'INVALID_INPUT');
  try {
    const snap = await getDoc(doc(db, COLLECTIONS.HEALTH_RECORDS, recordId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    throw new FirestoreError('No se pudo cargar la observación', err);
  }
};

/**
 * Suscripción reactiva a TODOS los records activos del hogar.
 * El caller filtra/sortea por pet en memoria — la cantidad esperada
 * (≤10 pets × ~20 records cada uno) es chica para beta.
 */
export const subscribeToHealthRecords = (familyId, onNext, onError) => {
  if (!familyId) {
    onError?.(new AppError('familyId requerido', 'INVALID_INPUT'));
    return () => {};
  }
  const recordsRef = collection(db, COLLECTIONS.HEALTH_RECORDS);
  const q = query(recordsRef, where('familyId', '==', familyId));

  return onSnapshot(
    q,
    (snapshot) => {
      const records = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => !r.deletedAt);
      onNext(records);
    },
    (err) => {
      onError?.(new FirestoreError('No se pudieron cargar las observaciones', err));
    }
  );
};
